import { useEffect, useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useLocation, useNavigate } from 'react-router-dom'
import type { ColDef, ColGroupDef } from 'ag-grid-community'
import { startCase } from 'lodash-es'

import { QRADatasetRowEntry, QRARowEntryCRID } from '$/types'
import { useAppSelector } from 'app/hooks'
import {
  IGetQRATableQueryParams,
  useAddQRATableExportJobMutation,
  useGetQRATableQuery
} from 'app/services/api/qra/datatable'
import {
  IGetQRAGraphsQueryParams,
  IQRAGraphCsvExportParams
} from 'app/services/api/qra/graphs'
import { useGetQRASavedStateQuery } from 'app/services/api/qra/savedstates'
import { selectSystemScopeIdContainer } from 'app/layout/Navbar/Filters/globalFilterSelectors'
import { useToggleRiskDensity } from 'common/hooks/useToggleRiskDensity'
import { QRAViewType } from 'common/utils/navigation/qraNavUtils'

import { useQraGraphOverride } from '_/src/features/RiskManagement/Components/Graphs/useQraGraphOverride'
import { IQRAGraphBools } from 'features/RiskManagement/Components/Graphs'
import {
  selectRiskUnit,
  selectSavedFilter
} from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'
import { IGroupColumnsSelection } from 'features/RiskManagement/Components/Tables'
import TableView from 'features/RiskManagement/Components/Tables/Layout/SystemView'
import { convertTableAPIDataToQRATableComponentProps } from 'features/RiskManagement/Components/Tables/utils/data'

import GraphsViewWrapper from './graphsViewWrapper'
import { ISavedStateGraphsInitProps, ISavedStateTableInitProps } from './types'

// #region System Graphs View
const SystemGraphsInit = ({
  qraBasePath,
  qraViewBasePath,
  stateId,
  assessmentId
}: ISavedStateGraphsInitProps) => {
  const activeFilterProfileIdContainer = useAppSelector(
    selectSystemScopeIdContainer
  )

  const localFilter = useAppSelector(selectSavedFilter)
  const riskUnit = useAppSelector(selectRiskUnit)

  const { currentState } = useGetQRASavedStateQuery(stateId ?? skipToken, {
    selectFromResult: (result) => ({
      currentState: result.currentData
    })
  })

  const graphSelections: IQRAGraphBools = {
    riskbyconsequence: true,
    riskbyline: true,
    riskforecast: true
  }

  const shouldSkipGraphs =
    activeFilterProfileIdContainer.id == null || currentState == null

  const qraGraphsAPIParams: IGetQRAGraphsQueryParams = {
    systemScopeId: activeFilterProfileIdContainer.id?.toString() ?? '',
    localFilter,
    riskUnit,
    savedStateId1: stateId,
    assessmentId,
    graphSelections
  }

  const {
    charts,
    riskByPipelineLengthGraphData,
    riskBySystemGraphData,
    isFetching,
    graphLoadingByType
  } = useQraGraphOverride({
    graphsQueryParams: qraGraphsAPIParams,
    skip: shouldSkipGraphs,
    refetchOn: activeFilterProfileIdContainer
  })

  const graphCsvExportParams: IQRAGraphCsvExportParams = {
    systemScopeId: activeFilterProfileIdContainer.id?.toString() ?? '',
    continuousRouteId: undefined,
    savedStateId: stateId,
    localFilter,
    riskUnit
  }

  return (
    <GraphsViewWrapper
      qraBasePath={qraBasePath}
      qraViewBasePath={qraViewBasePath}
      showLoading={isFetching}
      charts={charts}
      riskByPipelineLengthGraphData={riskByPipelineLengthGraphData}
      riskBySystemGraphData={riskBySystemGraphData}
      hasLineViewNavigation
      assessmentId={assessmentId}
      graphLoadingByType={graphLoadingByType}
      csvExportParams={graphCsvExportParams}
    />
  )
}
// #endregion

// #region System Table View
const SystemTableInit = ({
  qraViewBasePath,
  stateId,
  assessmentId
}: ISavedStateTableInitProps) => {
  useToggleRiskDensity()

  const navigate = useNavigate()
  const { search } = useLocation()

  const activeFilterProfileIdContainer = useAppSelector(
    selectSystemScopeIdContainer
  )

  const localFilter = useAppSelector(selectSavedFilter)
  const riskUnit = useAppSelector(selectRiskUnit)

  const { currentData: currentState } = useGetQRASavedStateQuery(
    stateId ?? skipToken
  )

  const [groupColumns, setGroupColumns] = useState<IGroupColumnsSelection[]>([
    { id: 0, name: 'Threat', groupTypeColumns: undefined }
  ])

  const qraTableQueryParams: IGetQRATableQueryParams = {
    systemScopeId: activeFilterProfileIdContainer.id?.toString() ?? '',
    localFilter,
    riskUnit,
    tableTypeSelections: groupColumns.map((group) => startCase(group.name)),
    savedStateId1: stateId,
    assessmentId: assessmentId
  }

  const {
    data: tableData,
    isFetching,
    isUninitialized,
    refetch
  } = useGetQRATableQuery(qraTableQueryParams, {
    skip: activeFilterProfileIdContainer.id == null || currentState == null
  })

  const [addQRATableExportJob] = useAddQRATableExportJobMutation()

  const rowSelectCB = (row: QRADatasetRowEntry<QRARowEntryCRID>): void => {
    navigate(
      `${qraViewBasePath}/line/${row.ContinuousRouteId}/${QRAViewType.Table}${search}`
    )
  }

  const [colDefs, rowData] = useMemo(
    () =>
      convertTableAPIDataToQRATableComponentProps<QRARowEntryCRID>(
        tableData,
        groupColumns,
        assessmentId != null
      ),
    [assessmentId, groupColumns, tableData]
  )

  const pinnedDefs = useMemo(
    () =>
      colDefs.map((colDef: ColDef | ColGroupDef) => {
        return {
          ...colDef,
          sortable: true,
          children: (colDef as ColGroupDef).children?.map((child) => ({
            ...child,
            sortable: true,
            pinned: (colDef as ColDef).pinned
          }))
        }
      }),
    [colDefs]
  )

  useEffect(() => {
    if (!isUninitialized) {
      refetch()
    }
  }, [activeFilterProfileIdContainer, isUninitialized, refetch])

  return (
    <TableView
      showLoading={isFetching}
      colDefs={pinnedDefs}
      rowData={rowData}
      groupColumns={groupColumns}
      setGroupColumns={setGroupColumns}
      rowSelectCB={rowSelectCB}
      mutationTrigger={addQRATableExportJob}
      exportParams={qraTableQueryParams}
    />
  )
}
// #endregion

export { SystemTableInit, SystemGraphsInit }
